import { Toast } from '@byecode/ui'
import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { type ActorEvent, DELAY_TYPE, ImageInfo, type IndependentAction, type RecordLikeProtocol } from '@lighthouse/core'
import {
    ANONYMOUS,
    DelayRunController,
    filterRecordChecker,
    readCustomViewStack,
    resolveFilter,
    SYSTEM_ROLE,
    useApplicationContext,
    useFillPickerContext
} from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { actionWorker } from '@/actionsEngine'
import type { ExecuteActionParams } from '@/actionsEngine/type'
import { appRoleAtom, deviceTypeAtom, otherPlatformListAtom } from '@/atoms/application/state'
import { dataSourcePoolAtom } from '@/atoms/dataSource/state'
import { pageBlockContextIndexAtom, pageQueryParameterFamily, pageStatesParameterFamily } from '@/atoms/page/state'
import { pageListAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentPageContext } from '@/contexts/PageContext'

import { useActionRuntimeParams } from './useActionRuntimeParams'
import { useCurrentAppId, useCurrentEnvId, useDeviceType } from './useApplication'
import { useParseVariable2Field4SelectDataSource, useParseVariableWithSubscription } from './useParseVariable'

export type TriggerArgs = {
    id: string
    actorEvent: ActorEvent
    scope?: string
    customViewRecord?: RecordLikeProtocol
    customViewStack?: ReturnType<typeof readCustomViewStack>
    options?: {
        formRecord?: RecordLikeProtocol
        viewRecord?: RecordLikeProtocol
        imageInfo?: ImageInfo
    }
}

interface ActionRuntimeContextValue {
    handleActionListTrigger: (
        payload: TriggerArgs & {
            blockId?: string
        }
    ) => Promise<void>
}

interface ActionRuntimeProviderProps {
    children?: React.ReactNode
}

const noopActionRuntimeContextValue: ActionRuntimeContextValue = {
    handleActionListTrigger: async () => {
        return
    }
}

const ActionRuntimeContext = createContext<ActionRuntimeContextValue>(noopActionRuntimeContextValue)

function useCreateActionRuntimeValue(): ActionRuntimeContextValue {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { fetchAVPlayUrl } = useApplicationContext()
    const { palettes } = useFillPickerContext()
    const { pageId: contextPageId } = useCurrentPageContext()
    const { getActionRuntimeParams } = useActionRuntimeParams()
    const parseVariable2Field = useParseVariableWithSubscription()
    const parseSelectDataSourceVariable2Field = useParseVariable2Field4SelectDataSource()
    const delayRunnerRef = useRef<DelayRunController | null>(null)

    useEffect(() => {
        return () => {
            delayRunnerRef.current?.dispose()
            delayRunnerRef.current = null
        }
    }, [])

    const getDelayRunner = useCallback(() => {
        if (!delayRunnerRef.current) {
            delayRunnerRef.current = new DelayRunController()
        }
        return delayRunnerRef.current
    }, [])

    const getRuntimeSnapshot = useAtomCallback(
        useCallback(
            get => {
                const workflowList = get(workflowListAtom)
                const pageStateParameterValues = get(pageStatesParameterFamily(contextPageId))
                const pageQueryParameterValues = get(pageQueryParameterFamily(contextPageId))
                const pageBlockContextIndex = get(pageBlockContextIndexAtom)
                const otherPlatformList = get(otherPlatformListAtom)
                const appRole = get(appRoleAtom)
                const userId = SYSTEM_ROLE.has(appRole) ? ANONYMOUS : appRole
                const pageList = get(pageListAtom)
                const dataSourceList = get(dataSourcePoolAtom).filter(item => item.appId === appId && item.envId === envId)
                return {
                    workflowList,
                    pageStateParameterValues,
                    pageQueryParameterValues,
                    pageBlockContextIndex,
                    otherPlatformList,
                    userId,
                    pageList,
                    dataSourceList
                }
            },
            [appId, contextPageId, envId]
        )
    )

    const handleActionList = useCallback(
        async (
            payload: TriggerArgs & {
                blockId?: string
            }
        ): Promise<void> => {
            const {
                actorEvent,
                options,
                blockId,
                scope: payloadScope,
                customViewRecord: payloadCustomViewRecord,
                customViewStack
            } = payload
            const { actions, type } = actorEvent
            const runtimeSnapshot = getRuntimeSnapshot()
            const actionRuntimeParams = getActionRuntimeParams()
            const dummyTriggerNodeParams: ExecuteActionParams = {
                eventType: type,
                appId,
                envId,
                userId: runtimeSnapshot.userId,
                blockId,
                dataSourceList: runtimeSnapshot.dataSourceList,
                pageList: runtimeSnapshot.pageList,
                pageBlockContextIndex: runtimeSnapshot.pageBlockContextIndex,
                pageStateParameterValues: runtimeSnapshot.pageStateParameterValues,
                pageQueryParameterValues: runtimeSnapshot.pageQueryParameterValues,
                palettes,
                workflowList: runtimeSnapshot.workflowList,
                otherPlatformList: runtimeSnapshot.otherPlatformList,
                scope: payloadScope,
                formRecord: options?.formRecord,
                viewRecord: options?.viewRecord || payloadCustomViewRecord,
                imageInfo: options?.imageInfo,
                parseVariable2Field,
                parseSelectDataSourceVariable2Field,
                fetchAVPlayUrl,
                ...actionRuntimeParams,
                customViewStack
            }

            try {
                await actionWorker.invokeList(
                    actions,
                    { ...dummyTriggerNodeParams, upstreamNodesResult: {} },
                    {
                        shouldExecute: (action: IndependentAction, currentParams: ExecuteActionParams) => {
                            const executeFilter = action.config?.executeFilter
                            if (!executeFilter?.expression?.conditions?.length) {
                                return true
                            }
                            const resolved = resolveFilter({
                                filter: executeFilter,
                                extraParams: currentParams,
                                useInFilterRecordChecker: true
                            })
                            if (!resolved?.expression?.conditions?.length) {
                                return false
                            }
                            return filterRecordChecker({
                                filter: resolved,
                                initStatus: false,
                                getVariableToFieldValue: val =>
                                    currentParams.parseVariable2Field(val, {
                                        viewRecord: currentParams.viewRecord,
                                        formRecord: currentParams.formRecord,
                                        upstreamNodesResult: currentParams.upstreamNodesResult,
                                        imageInfo: currentParams.imageInfo,
                                        scope: payloadScope
                                    })
                            })
                        }
                    }
                )
            } catch {
                Toast.error('执行失败')
            }
        },
        [
            appId,
            envId,
            fetchAVPlayUrl,
            getActionRuntimeParams,
            getRuntimeSnapshot,
            palettes,
            parseSelectDataSourceVariable2Field,
            parseVariable2Field
        ]
    )

    const handleActionListTrigger = useCallback(
        (
            payload: TriggerArgs & {
                blockId?: string
            }
        ): Promise<void> => {
            const {
                id,
                actorEvent,
                options,
                blockId,
                scope: payloadScope,
                customViewRecord: payloadCustomViewRecord,
                customViewStack
            } = payload
            const { config } = actorEvent
            const delayType = config?.delayRunConfig?.type ?? DELAY_TYPE.NONE
            const delayTime = config?.delayRunConfig?.time ?? 0

            const triggerPayload = {
                id,
                actorEvent,
                options,
                blockId,
                scope: payloadScope,
                customViewRecord: payloadCustomViewRecord,
                customViewStack
            }

            return getDelayRunner().run(id, delayType, delayTime, () => handleActionList(triggerPayload))
        },
        [getDelayRunner, handleActionList]
    )

    const handleActionListTriggerRef = useRef(handleActionListTrigger)

    useEffect(() => {
        handleActionListTriggerRef.current = handleActionListTrigger
    }, [handleActionListTrigger])

    const stableValueRef = useRef<ActionRuntimeContextValue>({
        handleActionListTrigger: payload => handleActionListTriggerRef.current(payload)
    })

    return stableValueRef.current
}

export const ActionRuntimeProvider = ({ children }: ActionRuntimeProviderProps) => {
    const value = useCreateActionRuntimeValue()
    return <ActionRuntimeContext.Provider value={value}>{children}</ActionRuntimeContext.Provider>
}

export const useActionTrigger = (blockId?: string) => {
    const runtime = useContext(ActionRuntimeContext)
    const { scope } = useContainerBlockContext()
    const customViewContext = useCustomViewBlockContext()
    const { record } = customViewContext
    const customViewStack = useMemo(() => readCustomViewStack(customViewContext), [customViewContext])
    const { handleActionListTrigger } = runtime

    const dispatchActionListTrigger = useCallback(
        (
            id: string,
            actorEvent: ActorEvent,
            options?: { formRecord?: RecordLikeProtocol; viewRecord?: RecordLikeProtocol; imageInfo?: ImageInfo }
        ) => {
            return handleActionListTrigger({
                id,
                actorEvent,
                options,
                blockId,
                scope,
                customViewRecord: options?.viewRecord || record,
                customViewStack
            })
        },
        [blockId, customViewStack, handleActionListTrigger, record, scope]
    )

    return {
        handleActionListTrigger: dispatchActionListTrigger
    }
}
